<template>
  <div class="content cms-preview-screen">
    <div class="col-md-12 cms-preview-header">
      <base-button
        type="primary"
        round
        icon
        class="top-button-back"
        @click="goBack"
      >
        <i class="fa fa-arrow-left"></i>
      </base-button>

      <div class="cms-title-group">
        <h2 class="text-eveer">CMS Preview</h2>
        <p>{{ pageLabel }}</p>
      </div>

      <base-button
        v-if="role == 'Admin'"
        type="primary"
        class="cms-edit-button"
        :disabled="loading"
        @click="editPage"
      >
        <i :class="pageId ? 'fa fa-edit' : 'fa fa-plus'"></i>
        {{ pageId ? "Edit" : "Create" }}
      </base-button>
    </div>

    <div class="row">
      <div class="col-lg-4 col-md-12">
        <card class="cms-summary-card">
          <h4 slot="header" class="card-title">Page Details</h4>

          <div class="cms-meta-list">
            <div class="cms-meta-item">
              <span class="cms-meta-label">Title</span>
              <strong>{{ pageTitle }}</strong>
            </div>

            <div class="cms-meta-item">
              <span class="cms-meta-label">Page</span>
              <strong>{{ pageLabel }}</strong>
            </div>

            <div class="cms-meta-item">
              <span class="cms-meta-label">Slug</span>
              <strong>{{ pageSlug || "-" }}</strong>
            </div>

            <div class="cms-meta-item">
              <span class="cms-meta-label">Status</span>
              <span class="cms-status" :class="pageStatus">
                {{ pageStatus }}
              </span>
            </div>

            <div class="cms-meta-item">
              <span class="cms-meta-label">Updated</span>
              <strong>{{ formattedUpdatedAt }}</strong>
            </div>
          </div>
        </card>
      </div>

      <div class="col-lg-8 col-md-12">
        <card class="cms-preview-card">
          <div slot="header" class="cms-preview-card-header">
            <h4 class="card-title">Rendered Page</h4>
            <span class="cms-content-state" :class="{ empty: !hasContent }">
              {{ hasContent ? "Content available" : "No content" }}
            </span>
          </div>

          <div class="cms-device-frame">
            <div class="cms-device-bar">
              <span></span>
              <span></span>
              <span></span>
              <strong>{{ pageSlug || "cms-page" }}</strong>
            </div>

            <div
              v-if="hasContent"
              class="cms-rendered-content"
              v-html="previewHtml"
            ></div>

            <div v-else class="cms-empty-preview">
              <i class="fa fa-file-alt"></i>
              <h5>{{ pageTitle }}</h5>
              <p>This CMS page has not been created yet.</p>
            </div>
          </div>
        </card>
      </div>
    </div>

    <div class="full-screen-loader" v-if="loading">
      <div class="sk-chase">
        <div class="sk-chase-dot"></div>
        <div class="sk-chase-dot"></div>
        <div class="sk-chase-dot"></div>
        <div class="sk-chase-dot"></div>
        <div class="sk-chase-dot"></div>
        <div class="sk-chase-dot"></div>
      </div>
    </div>
  </div>
</template>

<script>
const API_ENDPOINTS = {
  getBySlug: "cms-data/get-by-slug/",
};

const PAGE_TYPES = [
  { label: "About Us", page_type: "about-us", slug: "about-us" },
  {
    label: "Terms & Conditions",
    page_type: "terms-conditions",
    slug: "terms-conditions",
  },
  { label: "Privacy Policy", page_type: "privacy-policy", slug: "privacy-policy" },
  { label: "Refund Policy", page_type: "refund-policy", slug: "refund-policy" },
];

export default {
  data() {
    return {
      role: window.localStorage.getItem("role"),
      loading: false,
      page: {},
    };
  },
  computed: {
    pageId() {
      return this.page._id || this.page.id || "";
    },
    pageType() {
      return (
        this.page.page_type ||
        this.page.type ||
        this.page.key ||
        this.$route.query.page_type ||
        ""
      );
    },
    pageSlug() {
      return (
        this.page.slug ||
        this.$route.query.slug ||
        this.getSlugFromPageType(this.pageType)
      );
    },
    pageLabel() {
      const pageType = PAGE_TYPES.find(
        (item) => item.page_type === this.pageType || item.slug === this.pageSlug
      );
      return pageType ? pageType.label : this.pageType || "CMS Page";
    },
    pageTitle() {
      return (
        this.page.title ||
        this.page.page_title ||
        this.page.name ||
        this.pageLabel ||
        "CMS Page"
      );
    },
    pageStatus() {
      if (this.page.status) return this.page.status.toString().toLowerCase();
      if (this.page.active_status === false || this.page.is_active === false) {
        return "inactive";
      }
      return this.pageId ? "active" : "not-created";
    },
    previewHtml() {
      return (
        this.page.html ||
        this.page.content ||
        this.page.description ||
        this.page.body ||
        ""
      );
    },
    hasContent() {
      return Boolean(this.stripContent(this.previewHtml));
    },
    formattedUpdatedAt() {
      return this.formatDate(
        this.page.updated_at ||
          this.page.updatedAt ||
          this.page.created_at ||
          this.page.createdAt
      );
    },
  },
  async mounted() {
    await this.loadCmsPage();
  },
  methods: {
    async loadCmsPage() {
      const pageType = this.$route.query.page_type || "";
      const slug = this.$route.query.slug || this.getSlugFromPageType(pageType);

      if (!slug) {
        this.$apiService.notify("Page slug is required.");
        this.goBack();
        return;
      }

      this.loading = true;
      try {
        const response = await this.$apiService.getCall(
          API_ENDPOINTS.getBySlug + slug
        );

        if (response.isError || this.hasApiFailure(response.apidata)) {
          this.setEmptyPage(pageType, slug);
          return;
        }

        const page = this.extractRecord(response.apidata);
        if (!page) {
          this.setEmptyPage(pageType, slug);
          return;
        }

        this.page = {
          ...page,
          page_type: page.page_type || pageType || page.slug || page.type || "",
          slug,
        };
      } finally {
        this.loading = false;
      }
    },
    extractRecord(apiData) {
      const candidates = [
        apiData && apiData.Data,
        apiData && apiData.data,
        apiData && apiData.cms,
        apiData && apiData.page,
        apiData && apiData.result,
      ];

      for (const item of candidates) {
        if (!item) continue;
        if (Array.isArray(item)) return item[0];
        if (item.page) return item.page;
        if (item.cms) return item.cms;
        return item;
      }

      return null;
    },
    setEmptyPage(pageType, slug) {
      const page = PAGE_TYPES.find(
        (item) => item.page_type === pageType || item.slug === slug
      );

      this.page = {
        title: page ? page.label : "CMS Page",
        page_type: pageType || (page && page.page_type) || slug,
        slug,
        status: "not-created",
        content: "",
      };
    },
    stripContent(html) {
      return (html || "")
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, "")
        .trim();
    },
    getSlugFromPageType(pageType) {
      const page = PAGE_TYPES.find((item) => item.page_type === pageType);
      return page ? page.slug : pageType;
    },
    editPage() {
      const route = this.pageId ? "edit" : "add";
      const query = [
        "id=" + encodeURIComponent(this.pageId || -1),
        "slug=" + encodeURIComponent(this.pageSlug || ""),
        "page_type=" + encodeURIComponent(this.pageType || ""),
      ].join("&");

      this.$router.push(route + "?" + query);
    },
    goBack() {
      this.$router.push("/cms/list");
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";
      return date.toLocaleDateString();
    },
    hasApiFailure(apiData) {
      return apiData && apiData.Code && apiData.Code !== "P00001";
    },
  },
};
</script>

<style scoped>
.cms-preview-screen {
  min-height: calc(100vh - 120px);
}

.cms-preview-header {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 18px;
}

.cms-title-group h2 {
  margin: 0;
}

.cms-title-group p {
  color: #adb5bd;
  margin: 2px 0 0;
}

.cms-edit-button {
  margin-left: auto;
}

.cms-summary-card {
  height: calc(100% - 30px);
}

.cms-meta-list {
  display: flex;
  flex-direction: column;
}

.cms-meta-item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 15px 0;
  word-break: break-word;
}

.cms-meta-item:first-child {
  padding-top: 0;
}

.cms-meta-item:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.cms-meta-label {
  color: #8b92a9;
  font-size: 11px;
  letter-spacing: 0;
  text-transform: uppercase;
}

.cms-status {
  border-radius: 20px;
  display: inline-block;
  font-size: 12px;
  padding: 4px 10px;
  text-transform: capitalize;
  width: max-content;
}

.cms-status.active {
  background: rgba(0, 242, 195, 0.12);
  color: #00f2c3;
}

.cms-status.inactive {
  background: rgba(253, 93, 147, 0.12);
  color: #fd5d93;
}

.cms-status.not-created {
  background: rgba(255, 255, 255, 0.08);
  color: #adb5bd;
}

.cms-preview-card-header {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: space-between;
}

.cms-preview-card-header .card-title {
  margin-bottom: 0;
}

.cms-content-state {
  background: rgba(0, 242, 195, 0.12);
  border-radius: 20px;
  color: #00f2c3;
  font-size: 12px;
  padding: 5px 12px;
}

.cms-content-state.empty {
  background: rgba(255, 255, 255, 0.08);
  color: #adb5bd;
}

.cms-device-frame {
  background: #050507;
  border: 1px solid #2b3553;
  border-radius: 8px;
  overflow: hidden;
}

.cms-device-bar {
  align-items: center;
  background: #1f2233;
  color: #adb5bd;
  display: flex;
  gap: 8px;
  min-height: 38px;
  padding: 0 14px;
}

.cms-device-bar span {
  background: #6c7293;
  border-radius: 50%;
  display: block;
  height: 9px;
  width: 9px;
}

.cms-device-bar strong {
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
  margin-left: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cms-rendered-content {
  background: #000000;
  color: #ffffff;
  min-height: 460px;
  overflow-x: auto;
  padding: 24px;
  word-break: break-word;
}

.cms-empty-preview {
  align-items: center;
  color: #adb5bd;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 460px;
  padding: 32px;
  text-align: center;
}

.cms-empty-preview i {
  color: #ba54f5;
  font-size: 36px;
  margin-bottom: 14px;
}

.cms-empty-preview h5 {
  color: #ffffff;
  margin-bottom: 6px;
}

.cms-empty-preview p {
  margin: 0;
}

@media (max-width: 767px) {
  .cms-edit-button {
    margin-left: 0;
    width: 100%;
  }

  .cms-rendered-content,
  .cms-empty-preview {
    min-height: 360px;
    padding: 18px;
  }
}
</style>
